/**
 * CompleteBusinessProfileModal
 *
 * A modal that opens when ?isOpenbussinessModal=true is in the URL
 * to prompt business users to complete their profile.
 */
import React, { useState, useEffect } from 'react';
import { bool, func, object } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import {
  isUploadImageOverLimitError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import { businessCategories } from '../../config/configListing';

import {
  Form,
  Modal,
  Button,
  Avatar,
  IconSpinner,
  ImageFromFile,
  FieldTextInput,
  FieldSelect,
  FieldLocationAutocompleteInput,
  InlineTextButton,
  NamedLink,
} from '../../components';

import css from './CompleteBusinessProfileModal.module.css';

const ACCEPT_IMAGES = 'image/*';
const MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

const CompleteBusinessProfileModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    currentUser,
    onImageUpload,
    uploadInProgress,
    uploadImageError,
    image,
    onUpdateProfile,
    updateInProgress,
    updateProfileError,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    intl,
  } = props;

  const [uploadDelay, setUploadDelay] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [imageChanged, setImageChanged] = useState(false);

  const user = ensureCurrentUser(currentUser);
  const { bio, publicData } = user?.attributes?.profile || {};
  const { storeName, businessAddress, primaryBusinessCategory, zipCode } = publicData || {};
  const emailVerified = user?.attributes?.emailVerified;

  // Check which fields are missing
  const hasProfileImage = !!user.profileImage?.id;
  const hasStoreName = !!storeName;
  const hasBusinessAddress = !!businessAddress;
  const hasZipCode = !!zipCode;
  const hasBio = !!bio;
  const hasEmailVerified = !!emailVerified;
  const hasPrimaryBusinessCategory = !!primaryBusinessCategory?.value;

  // Check if profile is complete (all required fields filled)
  const isProfileComplete =
    hasProfileImage &&
    hasStoreName &&
    hasBusinessAddress &&
    hasZipCode &&
    hasBio &&
    hasEmailVerified &&
    hasPrimaryBusinessCategory;

  // Set initial values when user data is available
  useEffect(() => {
    if (user?.id) {
      setInitialValues({
        storeName: storeName || '',
        businessAddress: businessAddress || null,
        bio: bio || '',
        primaryBusinessCategory: primaryBusinessCategory?.value || '',
        zipCode: zipCode || '',
      });
    }
  }, [currentUser]);

  // Handle upload delay for UI feedback
  useEffect(() => {
    if (uploadInProgress === false && uploadDelay) {
      const timer = setTimeout(() => setUploadDelay(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [uploadInProgress]);

  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (file && file.size > MAX_IMAGE_SIZE_BYTES) {
      e.target.value = null;
      window.alert('File too large. Maximum size is 20MB.');
      return;
    }
    if (file != null) {
      setUploadDelay(true);
      setImageChanged(true);
      const tempId = `${file.name}_${Date.now()}`;
      onImageUpload({ id: tempId, file });
    }
  };

  const handleSubmit = async values => {
    const { storeName, businessAddress, bio, primaryBusinessCategory, zipCode } = values;

    // Find the selected category
    const selectedCategory = businessCategories.find(cat => cat.key === primaryBusinessCategory);
    const categoryValue = selectedCategory
      ? { value: selectedCategory.key, label: selectedCategory.label }
      : null;

    const uploadedImage = image;
    const profileImageInCloudnary =
      uploadedImage && uploadedImage.cloudProfileImage && uploadedImage.cloudProfileImage.secure_url
        ? { cloudProfileImage: uploadedImage.cloudProfileImage }
        : {};

    const profile = {
      bio,
      publicData: {
        storeName,
        businessAddress,
        primaryBusinessCategory: categoryValue,
        zipCode,
        isCompleteProfileModalShown: true,
        ...profileImageInCloudnary,
      },
    };

    // Update profileImage only if file system has been accessed
    const updatedValues =
      uploadedImage && uploadedImage.imageId && uploadedImage.file
        ? { ...profile, profileImageId: uploadedImage.imageId }
        : profile;

    try {
      await onUpdateProfile(updatedValues);
      // Close modal after successful submission
      onClose();
    } catch (e) {
      // Error will be handled by updateProfileError prop
      console.error('Profile update failed:', e);
    }
  };

  // Email verification resend error message
  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'CompleteBusinessProfileModal.resendFailedTooManyRequests'
    : 'CompleteBusinessProfileModal.resendFailed';

  // Profile image handling
  const profileImageId = user.profileImage ? user.profileImage.id : null;
  const profileImage = image || { imageId: profileImageId };
  const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
  const transientUser = { ...user, profileImage: transientUserProfileImage };

  const hasUploadError = !!uploadImageError && !uploadInProgress;
  const errorClasses = classNames({ [css.avatarUploadError]: hasUploadError });

  const fileExists = !!profileImage.file;
  const fileUploadInProgress = uploadInProgress && fileExists;
  const delayAfterUpload = profileImage.imageId && uploadDelay;

  const uploadingOverlay =
    uploadInProgress || uploadDelay ? (
      <div className={css.uploadingImageOverlay}>
        <IconSpinner />
      </div>
    ) : null;

  const imageFromFile =
    fileExists && (fileUploadInProgress || delayAfterUpload) ? (
      <ImageFromFile
        id={profileImage.id}
        className={errorClasses}
        rootClassName={css.uploadingImage}
        aspectWidth={1}
        aspectHeight={1}
        file={profileImage.file}
      >
        {uploadingOverlay}
      </ImageFromFile>
    ) : null;

  const avatarClasses = classNames(errorClasses, css.avatar, {
    [css.avatarInvisible]: uploadDelay,
  });

  const avatarComponent =
    !fileUploadInProgress && profileImage.imageId ? (
      <Avatar
        className={avatarClasses}
        renderSizes="96px"
        user={transientUser}
        disableProfileLink
      />
    ) : null;

  const showProfileImageField = !hasProfileImage || imageChanged;

  // If profile is complete, show success message
  if (isProfileComplete) {
    return null
  }

  return (
    <Modal
      id="CompleteBusinessProfileModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      containerClassName={css.modalContainer}
      className={css.CompleteBusinessProfileModal}
    >
      <div className={css.modalContent}>
        <h2 className={css.modalTitle}>
          <FormattedMessage id="CompleteBusinessProfileModal.title" />
        </h2>
        <p className={css.modalSubtitle}>
          <FormattedMessage id="CompleteBusinessProfileModal.subtitle" />
        </p>

        <FinalForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          render={fieldRenderProps => {
            const { handleSubmit, pristine } = fieldRenderProps;

            // Labels
            const storeNameLabel = intl.formatMessage({
              id: 'CompleteBusinessProfileModal.businessNameLabel',
            });
            const storeNamePlaceholder = intl.formatMessage({
              id: 'CompleteBusinessProfileModal.businessNamePlaceholder',
            });
            const businessAddressLabel = intl.formatMessage({
              id: 'CompleteBusinessProfileModal.businessAddressLabel',
            });
            const businessAddressPlaceholder = intl.formatMessage({
              id: 'CompleteBusinessProfileModal.businessAddressPlaceholder',
            });
            const bioLabel = intl.formatMessage({ id: 'CompleteBusinessProfileModal.bioLabel' });
            const bioPlaceholder = intl.formatMessage({
              id: 'CompleteBusinessProfileModal.bioPlaceholder',
            });
            const zipCodeLabel = intl.formatMessage({ id: 'ProfileSettingsPage.zipCodeLabel' });
            const zipCodePlaceholder = intl.formatMessage({ id: 'SignupForm.zipCodePlaceHolder' });
            const primaryCategoryLabel = intl.formatMessage({
              id: 'CompleteBusinessProfileModal.primaryCategoryLabel',
            });

            const submitDisabled =
              (pristine && !imageChanged) || updateInProgress || uploadInProgress;

            return (
              <Form className={css.form} onSubmit={handleSubmit}>
                {/* Profile Photo Upload - only show if missing */}
                {!hasProfileImage && (
                  <div className={css.profileImageSection}>
                    <Field
                      accept={ACCEPT_IMAGES}
                      id="profileImage"
                      name="profileImage"
                      type="file"
                      form={null}
                    >
                      {fieldProps => {
                        const { accept, id, input } = fieldProps;
                        const { name, type } = input;

                        let uploadError = null;
                        if (isUploadImageOverLimitError(uploadImageError)) {
                          uploadError = (
                            <div className={css.error}>
                              <FormattedMessage id="CompleteBusinessProfileModal.imageUploadFailedFileTooLarge" />
                            </div>
                          );
                        } else if (uploadImageError) {
                          uploadError = (
                            <div className={css.error}>
                              <FormattedMessage id="CompleteBusinessProfileModal.imageUploadFailed" />
                            </div>
                          );
                        }

                        return (
                          <div className={css.uploadAvatarWrapper}>
                            <label className={css.avatarLabel} htmlFor={id}>
                              {showProfileImageField && (imageFromFile || avatarComponent) ? (
                                <div className={css.avatarContainer}>
                                  {imageFromFile}
                                  {avatarComponent}
                                </div>
                              ) : (
                                <div className={css.avatarPlaceholder}>
                                  <div className={css.uploadIcon}>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="52"
                                      height="52"
                                      viewBox="0 0 52 52"
                                      fill="none"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M25.9998 5.41663C23.7008 5.41609 21.4507 6.08059 19.5208 7.33002C17.5909 8.57945 16.0637 10.3604 15.1232 12.4583C14.9917 12.7557 14.8574 13.0518 14.7202 13.3466L14.5728 13.6326C14.5012 13.7503 14.4078 13.8533 14.2977 13.936C14.1768 14.0023 14.0445 14.0449 13.9077 14.0616C13.8297 14.0768 13.6693 14.0833 12.9998 14.0833C10.9886 14.0833 9.05976 14.8822 7.63761 16.3044C6.21546 17.7266 5.4165 19.6554 5.4165 21.6666C5.4165 23.6779 6.21546 25.6067 7.63761 27.0289C9.05976 28.451 10.9886 29.25 12.9998 29.25H14.4558L16.6225 27.0833H12.9998C11.5632 27.0833 10.1855 26.5126 9.16968 25.4968C8.15385 24.481 7.58317 23.1032 7.58317 21.6666C7.58317 20.23 8.15385 18.8523 9.16968 17.8365C10.1855 16.8206 11.5632 16.25 12.9998 16.25H13.1168C13.6065 16.25 14.0117 16.25 14.3345 16.185C14.7245 16.107 15.1102 15.99 15.4742 15.7516C15.8382 15.5176 16.1025 15.2143 16.3365 14.8893C16.4622 14.716 16.5748 14.4906 16.6767 14.2761C16.7836 14.0465 16.9229 13.7402 17.0948 13.3575L17.0992 13.3466C17.8682 11.6291 19.1177 10.1708 20.6971 9.14751C22.2764 8.12426 24.118 7.57979 25.9998 7.57979C27.8817 7.57979 29.7233 8.12426 31.3026 9.14751C32.8819 10.1708 34.1315 11.6291 34.9005 13.3466L34.9048 13.3596C35.0767 13.741 35.2161 14.0465 35.323 14.2761C35.4248 14.4906 35.5375 14.7138 35.6632 14.8893C35.895 15.2143 36.1615 15.5176 36.5255 15.7538C36.8895 15.99 37.2752 16.107 37.6652 16.1871C37.988 16.2521 38.3932 16.2521 38.8828 16.25H38.9998C40.4364 16.25 41.8142 16.8206 42.83 17.8365C43.8458 18.8523 44.4165 20.23 44.4165 21.6666C44.4165 23.1032 43.8458 24.481 42.83 25.4968C41.8142 26.5126 40.4364 27.0833 38.9998 27.0833H35.3772L37.5438 29.25H38.9998C39.9957 29.25 40.9818 29.0538 41.9019 28.6727C42.8219 28.2916 43.6579 27.733 44.3621 27.0289C45.0662 26.3247 45.6248 25.4887 46.0059 24.5686C46.387 23.6486 46.5832 22.6625 46.5832 21.6666C46.5832 20.6708 46.387 19.6847 46.0059 18.7646C45.6248 17.8446 45.0662 17.0086 44.3621 16.3044C43.6579 15.6002 42.8219 15.0416 41.9019 14.6605C40.9818 14.2794 39.9957 14.0833 38.9998 14.0833C38.3303 14.0833 38.17 14.0768 38.0942 14.0616C37.9572 14.0443 37.8248 14.0009 37.7042 13.9338C37.5932 13.8513 37.4991 13.7483 37.4268 13.6305L37.3922 13.572L37.2795 13.3445C37.1423 13.0504 37.008 12.755 36.8765 12.4583C35.936 10.3604 34.4087 8.57945 32.4788 7.33002C30.549 6.08059 28.2989 5.41609 25.9998 5.41663Z"
                                        fill="black"
                                      />
                                      <path
                                        d="M26.0001 26L25.2331 25.233L26.0001 24.4681L26.7671 25.233L26.0001 26ZM27.0834 45.5C27.0834 45.7873 26.9693 46.0628 26.7661 46.266C26.5629 46.4692 26.2874 46.5833 26.0001 46.5833C25.7128 46.5833 25.4372 46.4692 25.234 46.266C25.0309 46.0628 24.9167 45.7873 24.9167 45.5H27.0834ZM16.5664 33.8996L25.2331 25.233L26.7671 26.767L18.1004 35.4336L16.5664 33.8996ZM26.7671 25.233L35.4337 33.8996L33.8997 35.4336L25.2331 26.767L26.7671 25.233ZM27.0834 26V45.5H24.9167V26H27.0834Z"
                                        fill="black"
                                      />
                                    </svg>
                                  </div>
                                  <span className={css.addPhotoText}>
                                    <FormattedMessage id="CompleteBusinessProfileModal.addProfilePhoto" />
                                  </span>
                                </div>
                              )}
                            </label>
                            <Button
                              type="button"
                              className={css.choosePhotoButton}
                              onClick={() => document.getElementById(id).click()}
                            >
                              <FormattedMessage id="CompleteBusinessProfileModal.choosePhoto" />
                            </Button>
                            <input
                              accept={accept}
                              id={id}
                              name={name}
                              className={css.uploadAvatarInput}
                              disabled={uploadInProgress}
                              onChange={e => handleImageUpload(e)}
                              type={type}
                            />
                            {uploadError}
                          </div>
                        );
                      }}
                    </Field>
                  </div>
                )}

                {/* Business Name - only show if missing */}
                {!hasStoreName && (
                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id="storeName"
                    name="storeName"
                    label={storeNameLabel}
                    placeholder={storeNamePlaceholder}
                  />
                )}

                {/* Business Address - only show if missing */}
                {!hasBusinessAddress && (
                  <FieldLocationAutocompleteInput
                    id="businessAddress"
                    rootClassName={css.field}
                    inputClassName={css.locationAutocompleteInput}
                    iconClassName={css.locationAutocompleteInputIcon}
                    predictionsClassName={css.predictionsRoot}
                    predictionsAttributionClassName={css.poweredByGoogleHidden}
                    validClassName={css.validLocation}
                    name="businessAddress"
                    placeholder={businessAddressPlaceholder}
                    label={businessAddressLabel}
                    useDefaultPredictions={false}
                    format={v => v}
                  />
                )}

                {/* Zip Code - only show if missing */}
                {!hasZipCode && (
                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    label={zipCodeLabel}
                    placeholder={zipCodePlaceholder}
                  />
                )}

                {/* Bio - only show if missing */}
                {!hasBio && (
                  <FieldTextInput
                    className={css.field}
                    type="textarea"
                    id="bio"
                    name="bio"
                    label={bioLabel}
                    placeholder={bioPlaceholder}
                  />
                )}

                {/* Primary Category - only show if missing */}
                {!hasPrimaryBusinessCategory && (
                  <FieldSelect
                    className={css.field}
                    name="primaryBusinessCategory"
                    id="primaryBusinessCategory"
                    label={primaryCategoryLabel}
                  >
                    <option disabled value="">
                      {intl.formatMessage({
                        id: 'CompleteBusinessProfileModal.primaryCategoryPlaceholder',
                      })}
                    </option>
                    {businessCategories.map(category => (
                      <option key={category.key} value={category.key}>
                        {category.label}
                      </option>
                    ))}
                  </FieldSelect>
                )}

                {/* Email Verification - only show if not verified */}
                {!hasEmailVerified && (
                  <div className={css.emailVerificationSection}>
                    <span className={css.verifyEmailText}>
                      <FormattedMessage id="CompleteBusinessProfileModal.verifyEmail" />
                    </span>
                    {sendVerificationEmailInProgress ? (
                      <span className={css.sendingEmail}>
                        <FormattedMessage id="CompleteBusinessProfileModal.sendingEmail" />
                      </span>
                    ) : (
                      <InlineTextButton
                        rootClassName={css.resendEmailLink}
                        onClick={onResendVerificationEmail}
                      >
                        <FormattedMessage id="CompleteBusinessProfileModal.resendEmail" />
                      </InlineTextButton>
                    )}
                    {sendVerificationEmailError && (
                      <p className={css.error}>
                        <FormattedMessage id={resendErrorTranslationId} />
                      </p>
                    )}
                  </div>
                )}

                {/* Error Message */}
                {updateProfileError && (
                  <p className={css.error}>
                    <FormattedMessage id="CompleteBusinessProfileModal.updateProfileFailed" />
                  </p>
                )}

                {/* Submit Button */}
                <Button
                  className={css.submitButton}
                  type="submit"
                  inProgress={updateInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="CompleteBusinessProfileModal.saveChanges" />
                </Button>

                {/* View Profile Settings Link */}
                <NamedLink name="ProfileSettingsPage" className={css.viewProfileSettingsButton}>
                  <FormattedMessage id="CompleteBusinessProfileModal.viewProfileSettings" />
                </NamedLink>
              </Form>
            );
          }}
        />
      </div>
    </Modal>
  );
};

CompleteBusinessProfileModal.defaultProps = {
  currentUser: null,
  uploadImageError: null,
  updateProfileError: null,
  sendVerificationEmailError: null,
  image: null,
};

CompleteBusinessProfileModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  currentUser: propTypes.currentUser,
  onImageUpload: func.isRequired,
  uploadInProgress: bool.isRequired,
  uploadImageError: propTypes.error,
  image: object,
  onUpdateProfile: func.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  intl: intlShape.isRequired,
};

export default injectIntl(CompleteBusinessProfileModal);
